// Print MODES — the documents the office reaches for by name, each a preset
// over the print options (quotes.md 7a.13; Robert 2026-09-08: "this is Scope
// of Works, all settings applied, this is a ... and so on. 1.0 was hacky so
// 2.0 tries to clean this up"). A mode APPLIES its preset; the side panel then
// shows the adjustments that still make sense for that document. Tweak past
// the preset and the panel says "Custom" until a mode is picked again.
//
// `signature` = the keys that DEFINE the mode (detection compares these);
// `preset` = signature plus the resets applying the mode makes. Options not
// in the preset (bordered, hideZero, summary, headings…) carry over.
import { DEFAULT_PRINT_OPTIONS } from "./QuoteView.jsx";

const CLIENT_BASE = { sow: false, estimate: false, hidePrices: false, allDetails: false, showQty: false };

export const PRINT_MODES = [
  {
    key: "quote",
    label: "Quotation",
    hint: "The client quote: description and price, floor & room headings.",
    signature: { ...CLIENT_BASE, detail: "full", roomTotals: false, separateMargin: false },
    preset: { ...CLIENT_BASE, detail: "full", roomTotals: false, separateMargin: false, rateColumns: false },
  },
  {
    key: "estimate",
    label: "Estimate",
    hint: "The same letter with quote → estimate throughout the wording.",
    signature: { ...CLIENT_BASE, estimate: true },
    preset: { ...CLIENT_BASE, estimate: true, detail: "full", roomTotals: false, separateMargin: false, rateColumns: false },
  },
  {
    key: "sow",
    label: "Scope of Works",
    hint: "No prices, no quote wording: the works and the notes, for the client and the trades.",
    signature: { sow: true },
    preset: { sow: true, hidePrices: true, estimate: false, allDetails: false, showQty: false, detail: "full", summary: "none", roomTotals: false, separateMargin: false, rateColumns: false },
  },
  {
    key: "headerOnly",
    label: "Header sections only",
    hint: "Each quote header with its subtotal, no lines (1.0's Header Only).",
    signature: { ...CLIENT_BASE, detail: "summaryOnly" },
    preset: { ...CLIENT_BASE, detail: "summaryOnly", headings: "quote", roomTotals: false, separateMargin: false, rateColumns: false },
  },
  {
    key: "sections",
    label: "Room sections (AA / Vero)",
    hint: "Numbered room sections closing with a total each, qty & unit columns.",
    signature: { ...CLIENT_BASE, detail: "full", roomTotals: true, separateMargin: false },
    preset: { ...CLIENT_BASE, detail: "full", headings: "location", roomTotals: true, separateMargin: false, rateColumns: "unit" },
  },
  {
    key: "vero",
    label: "Room sections, cost + margin (Vero)",
    hint: "Room sections with line amounts at cost and one Builders Overheads and Profit line.",
    signature: { ...CLIENT_BASE, detail: "full", roomTotals: true, separateMargin: true },
    preset: { ...CLIENT_BASE, detail: "full", headings: "location", roomTotals: true, separateMargin: true, rateColumns: "unit" },
  },
  {
    key: "internal",
    label: "Internal (all details)",
    hint: "Unit, quantity and margin beside every line. Ours, never the client's.",
    signature: { allDetails: true },
    preset: { allDetails: true, sow: false, hidePrices: false, showQty: false },
  },
];

const byKey = Object.fromEntries(PRINT_MODES.map((m) => [m.key, m]));

export function modeByKey(key) {
  return byKey[key] || null;
}

/** The options with a mode's preset applied (and the mode recorded). */
export function applyMode(options, key) {
  const mode = byKey[key];
  if (!mode) return { ...options };
  return { ...DEFAULT_PRINT_OPTIONS, ...options, ...mode.preset, mode: key };
}

const same = (a, b) => (a === b) || (!a && !b && (a === false || a == null) && (b === false || b == null));

/** Which mode these options ARE — the first whose signature matches, else "custom". */
export function detectMode(options) {
  const o = { ...DEFAULT_PRINT_OPTIONS, ...options };
  // Most specific first: the SOW and internal flags outrank the client shapes.
  const order = ["sow", "internal", "headerOnly", "vero", "sections", "estimate", "quote"];
  for (const key of order) {
    const sig = byKey[key].signature;
    if (Object.keys(sig).every((k) => same(o[k], sig[k]))) return key;
  }
  return "custom";
}
